/**
 * Role-Based Access Control (RBAC) Utilities
 *
 * This file contains helper functions for checking user permissions
 * throughout the frontend application.
 */

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  SURGEON: 'surgeon',
  DATA_NURSE: 'data_nurse',
};

/**
 * Check if user has one or more specific roles
 * @param {Object} user - The user object from localStorage or context
 * @param {...string} roles - One or more roles to check against
 * @returns {boolean} True if user has any of the specified roles
 *
 * @example
 * hasRole(user, ROLES.ADMIN, ROLES.SURGEON) // true if user is admin OR surgeon
 */
export const hasRole = (user, ...roles) => {
  return user && roles.includes(user.role);
};

/**
 * Check if user is an admin
 * Admins have full system access
 */
export const isAdmin = (user) => {
  return user?.role === ROLES.ADMIN;
};

/**
 * Check if user is a surgeon
 * Surgeons have full clinical access
 */
export const isSurgeon = (user) => {
  return user?.role === ROLES.SURGEON;
};

/**
 * Check if user is a data nurse
 * Data nurses can view dashboards, fill/edit surveys, and enroll/edit patients
 */
export const isDataNurse = (user) => {
  return user?.role === ROLES.DATA_NURSE;
};

/**
 * Check if user has admin or surgeon privileges
 * These roles have the same permissions currently
 */
export const isSurgeonOrAdmin = (user) => {
  return hasRole(user, ROLES.ADMIN, ROLES.SURGEON);
};

// ==================== PERMISSION CHECKS ====================

/**
 * Dashboard & Navigation Permissions
 */
export const canViewDashboard = (_user) => true; // All roles can view dashboard
export const canViewPatientList = (_user) => true; // All roles can view patient list
export const canViewPatientDashboard = (_user) => true; // All roles can view patient dashboard
export const canViewAnalysis = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canViewNotifications = (_user) => true; // All roles
export const canViewSurgeonTasks = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canViewPatientCompliance = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canViewPatientComplications = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canViewPriorityList = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles, data nurses do the calling

/**
 * Patient Management Permissions
 */
export const canCreatePatient = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can create patients
export const canEditPatient = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can edit patients
export const canDeletePatient = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins

/**
 * Event/Surgery Management Permissions
 */
export const canCreateEvent = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can create surgery events
export const canEditEvent = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can edit surgery events
export const canDeleteEvent = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canCreateSurgery = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can create surgeries
export const canEditSurgery = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can edit surgeries
export const canCreateClinicVisit = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canEditClinicVisit = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canCreateComplication = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canEditComplication = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins

/**
 * Survey Permissions
 */
export const canFillSurvey = (_user) => true; // All roles can fill surveys
export const canEditSurvey = (_user) => true; // All roles can edit surveys
export const canCreateSurvey = (_user) => true; // All roles can create surveys
export const canViewSurvey = (_user) => true; // All roles can view surveys
export const canDeleteSurvey = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canResetSurvey = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canSendSurveyEmail = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can send survey emails
export const canSendSurveyReminder = (user) => hasRole(user, ROLES.ADMIN, ROLES.SURGEON, ROLES.DATA_NURSE); // All roles can send survey reminders

/**
 * User Management Permissions
 */
export const canInviteUsers = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canManageUsers = (user) => isAdmin(user); // Only admins (for future use)

/**
 * System Permissions
 */
export const canAccessAnalytics = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins
export const canExportData = (user) => isSurgeonOrAdmin(user); // Only surgeons/admins

/**
 * Surgery Metadata Permissions
 * Data nurses can only edit basic metadata (patient info, hand dominance, surgical time)
 * Surgeons and admins can edit all metadata categories
 */

// Categories that data nurses are allowed to fully edit
export const DATA_NURSE_ALLOWED_METADATA_CATEGORIES = [
  'patientInformationSurgicalTimeAndPersonnel',
  'handDominance',
];

// Check if user can edit all surgery metadata categories (surgeons/admins only)
export const canEditAllSurgeryMetadata = (user) => isSurgeonOrAdmin(user);

// Check if user can edit a specific metadata category
export const canEditSurgeryMetadataCategory = (user, category) => {
  // Surgeons and admins can edit everything
  if (isSurgeonOrAdmin(user)) {
    return true;
  }

  // Data nurses can only edit allowed categories
  if (isDataNurse(user)) {
    return DATA_NURSE_ALLOWED_METADATA_CATEGORIES.includes(category);
  }

  return false;
};

// Check if user can add new metadata entries for a category
export const canAddSurgeryMetadata = (user, category) => {
  return canEditSurgeryMetadataCategory(user, category);
};

// Check if user can delete metadata entries for a category
export const canDeleteSurgeryMetadata = (user, category) => {
  return canEditSurgeryMetadataCategory(user, category);
};

/**
 * Get user role display name
 */
export const getRoleDisplayName = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return 'Admin';
    case ROLES.SURGEON:
      return 'Surgeon';
    case ROLES.DATA_NURSE:
      return 'Data Nurse';
    default:
      return 'Unknown Role';
  }
};

/**
 * Get user role badge color (for UI)
 */
export const getRoleBadgeColor = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return 'danger'; // Red
    case ROLES.SURGEON:
      return 'primary'; // Blue
    case ROLES.DATA_NURSE:
      return 'success'; // Green
    default:
      return 'secondary'; // Gray
  }
};
